import React from 'react';
import { Close } from '@mui/icons-material';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import { useSWRConfig } from 'swr';
// eslint-disable-next-line import/no-cycle -- TODO: Fix this the next time the file is edited.
import useContributionDialog from '@/components/common/contribution/useContributionDialog';
import useOwnerOrgId from '@/lib/hooks/useOwnerOrgId';
import translate from '@/lib/intl/translate';
import { Dialogs, useDialogManager } from '@/modules/Dialogs';
import { useEntityDetails } from '@/modules/EntityDetails';
import { unlinkDonationFromPledge } from '@/services/contributions';
import type PledgeContribution from '@/types/api/PledgeContribution';
import { formatCurrency } from '@/utils/currency';
import { formatDate } from '@/utils/dates';
import showErrorMessage from '@/utils/toasts/showErrorMessage';
import showSuccessMessage from '@/utils/toasts/showSuccessMessage';

const CLASSES = {
  linkDonationButton: {
    label: 'p-0 type-body4',
    root: 'p-0',
  },
  linkDonationButtonSmall: {
    label: 'p-0 type-body3',
    root: 'p-0',
  },
};

type PledgeReconciledProps = {
  constituentId: string;
  displayName: string;
  donationId: string;
  pledgeData: {
    amountPledged: number;
    amountReceived: number;
    pledgeDueDateString: string;
    reconciledDonations?: {
      amount: number;
      donationDateString: string;
      donationId: string;
    }[];
  };
};

export default function PledgeReconciled({
  constituentId,
  displayName,
  donationId: pledgeDonationId,
  pledgeData: {
    amountPledged,
    amountReceived,
    pledgeDueDateString,
    reconciledDonations = [],
  },
}: PledgeReconciledProps) {
  const { closeDialog, openDialog } = useDialogManager();
  const { openDonationEntity } = useEntityDetails();
  const [viewedDonation, setViewedDonation] = React.useState(null);
  const ownerOrgId = useOwnerOrgId();
  const { mutate } = useSWRConfig();

  // @ts-expect-error TS(2345) TODO(ts-migrate): Argument of type '{ donationId: any; onClose: () =... Remove this comment to see the full error message
  useContributionDialog(viewedDonation, {
    // @ts-expect-error TS(2339) TODO(ts-migrate): Property 'donationId' does not exist on type 'neve... Remove this comment to see the full error message
    donationId: viewedDonation?.donationId,
    onClose: () => setViewedDonation(null),
    preSelectedDonorId: constituentId,
  });

  const handleLinkDonationClick = React.useCallback(() => {
    openDialog(Dialogs.LINK_TO_DONATIONS, {
      constituentId,
      displayName,
      pledgeDonationId,
    });
  }, [constituentId, displayName, pledgeDonationId]);

  const handleViewDonationClick = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
    (e: any) => {
      const { currentTarget } = e;
      const entityId = currentTarget.getAttribute('data-donation-id');

      openDonationEntity(constituentId, entityId);
    },
    [constituentId, reconciledDonations],
  );

  const handleUnlinkDonationClick = React.useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ts-migrate)
    (donationId: any) => {
      openDialog(Dialogs.CONFIRMATION, {
        children: translate('CONFIRM_REMOVE_LINKED_CONTRIBUTION'),
        confirmLabel: translate('REMOVE'),
        onCancel: closeDialog,
        onClose: closeDialog,
        onConfirm: async () => {
          try {
            await unlinkDonationFromPledge(
              ownerOrgId,
              pledgeDonationId,
              donationId,
              true,
            );
            showSuccessMessage({
              message: translate('CONTRIBUTION_UNLINK_SUCCESS'),
            });
            mutate<PledgeContribution>(
              `donations/${pledgeDonationId}`,
              (currentData) =>
                currentData?.pledgeData
                  ? {
                      ...currentData,
                      pledgeData: {
                        ...currentData.pledgeData,
                        reconciledDonations: (
                          currentData.pledgeData.reconciledDonations || []
                        ).filter(
                          (donation) => donation.donationId !== donationId,
                        ),
                      },
                    }
                  : currentData,
            );
            closeDialog();
          } catch (e) {
            showErrorMessage({
              message: translate('CONTRIBUTION_UNLINK_ERROR'),
            });
          }
        },
      });
    },
    [pledgeDonationId, ownerOrgId],
  );

  return (
    <div
      className="mb-4 rounded-lg bg-grey-100 p-4"
      data-testid="pledge-reconciled-details"
    >
      <div className="mb-3 flex items-center justify-between">
        <h3 className="font-medium type-body2">
          {translate('DONATION_RECONCILED')}
        </h3>
        <Button
          classes={CLASSES.linkDonationButton}
          data-testid="link-pledge-to-donation-button"
          onClick={handleLinkDonationClick}
        >
          {translate('CONTRIBUTION_LINK_TO_CONTRIBUTION')}
        </Button>
      </div>

      <div>
        <span className="text-[32px] leading-[48px]">
          {formatCurrency(amountReceived, true)}/
          {formatCurrency(amountPledged, true)}
        </span>
      </div>

      <span className="text-text-secondary type-caption1">
        {translate('DONATION_LAST_UPDATED', {
          date: formatDate(pledgeDueDateString),
        })}
      </span>

      {!!reconciledDonations.length && (
        <>
          <Divider className="my-4" />
          <h3 className="text-body4 mb-2 font-medium">
            {translate('CONTRIBUTION_LINKED_CONTRIBUTIONS')}
          </h3>
          {reconciledDonations.map(
            ({ amount, donationDateString, donationId }) => (
              <div
                key={donationId}
                className="mb-2 flex items-center justify-between last:mb-0"
                data-testid={`reconciled-donations-${donationId}`}
              >
                <div className="w-4/12 type-body3">
                  {formatCurrency(amount, true)}
                </div>
                <div className="w-5/12 text-center type-body3">
                  {formatDate(donationDateString)}
                </div>
                <div className="w-2/12 text-right type-body3">
                  <Button
                    classes={CLASSES.linkDonationButtonSmall}
                    data-donation-id={donationId}
                    data-testid={`edit-donation-${donationId}`}
                    onClick={handleViewDonationClick}
                  >
                    {translate('VIEW')}
                  </Button>
                </div>
                <div className="relative w-1/12">
                  <IconButton
                    aria-label={translate('CONTRIBUTION_UNLINK_DONATION')}
                    className="absolute -top-6"
                    data-testid={`unlink-reconciled-donation-${donationId}`}
                    disableRipple
                    onClick={() => handleUnlinkDonationClick(donationId)}
                    size="large"
                  >
                    <Close className="text-error-main" />
                  </IconButton>
                </div>
              </div>
            ),
          )}
        </>
      )}
    </div>
  );
}
